import { Action } from './action';

export const DECISION_REASONS_CONFIG_KEY = 'claim_item_decision_reasons';
export const DECISION_REASON_CODE_MAX_LENGTH = 64;
export const DECISION_REASON_LABEL_MAX_LENGTH = 100;

export interface Reason {
  code: string;
  label: string;
}

/** Tenant-configured reasons, one list per decision type. */
export interface Reasons {
  approve: Reason[];
  reject: Reason[];
}

export interface DecisionReasonsConfig extends Record<string, unknown> {
  approve?: Partial<Reason>[];
  reject?: Partial<Reason>[];
}

export type RetrieveDecisionReasonsResponse = Reasons;
export type UpdateDecisionReasonsRequest = Reasons;
export type UpdateDecisionReasonsResponse = Reasons;

export const DEFAULT_DECISION_REASONS: Reasons = {
  approve: [
    { code: 'COVERED_REPAIR', label: 'Covered repair' },
    { code: 'PARTIALLY_COVERED', label: 'Partially covered' },
  ],
  reject: [
    { code: 'NOT_COVERED', label: 'Not covered by the contract' },
    { code: 'PRE_EXISTING_CONDITION', label: 'Pre-existing condition' },
    { code: 'WEAR_AND_TEAR', label: 'Normal wear and tear' },
    { code: 'LACK_OF_MAINTENANCE', label: 'Lack of maintenance' },
    { code: 'NO_FAILURE_FOUND', label: 'No failure found' },
    { code: 'OUTSIDE_COVERAGE_TERM', label: 'Outside the coverage term' },
  ],
};

export function normalizeDecisionReasonCode(code?: string | null): string | null {
  const normalized = code?.trim().toUpperCase();
  return normalized ? normalized : null;
}

/** Trims, upper-cases codes, and drops blank, too-long or duplicate entries. */
export function normalizeDecisionReasons(reasons?: ReadonlyArray<Partial<Reason> | null | undefined> | null): Reason[] {
  const seen = new Set<string>();
  const normalized: Reason[] = [];

  for (const reason of reasons ?? []) {
    const code = normalizeDecisionReasonCode(reason?.code);
    const label = reason?.label?.trim();

    if (!code || !label) continue;
    if (code.length > DECISION_REASON_CODE_MAX_LENGTH || label.length > DECISION_REASON_LABEL_MAX_LENGTH) continue;
    if (seen.has(code)) continue;

    seen.add(code);
    normalized.push({ code, label });
  }

  return normalized;
}

/** The tenant's reasons; a list that is missing or empty falls back to the defaults. */
export function getDecisionReasons(config?: DecisionReasonsConfig | null): Reasons {
  const approve = normalizeDecisionReasons(config?.approve);
  const reject = normalizeDecisionReasons(config?.reject);

  return {
    approve: approve.length ? approve : DEFAULT_DECISION_REASONS.approve,
    reject: reject.length ? reject : DEFAULT_DECISION_REASONS.reject,
  };
}

/** The configured reason for a decision, or undefined when the code isn't allowed for it. */
export function findDecisionReason(
  reasons: Reasons,
  action: Action.APPROVED | Action.REJECTED,
  code?: string | null
): Reason | undefined {
  const normalized = normalizeDecisionReasonCode(code);
  if (!normalized) return undefined;

  return (action === Action.APPROVED ? reasons.approve : reasons.reject).find(reason => reason.code === normalized);
}
